/* eslint-disable prettier/prettier */
import { Injectable, OnDestroy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { EventPublisherService } from '@bootstrap/services/event-publisher.service';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { LoggerService } from '@core/services/logger/logger.service';
import { combineQueries } from '@datorama/akita';
import { OrderQuery } from '@modules/order/state/order-query';
import type { AdditionalInfoFieldKey } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { extractAdditionalInfoIds } from '@modules/rx-intuitive-crown-additional-info/utils/extract-additional-info-ids';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { TeethManagerService } from '@modules/tooth-editor/state/teeth-manager-service';
import { TranslateService } from '@ngx-translate/core';
import { ImplantsViewService } from '@shared/implants-view/implants-view.service';
import { RoleTypeEnum } from '@shared/models/role-type';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { getTeethNumberingSystem } from '@shared/utils/teeth-numbering-system-util';
import { BehaviorSubject, combineLatest, defer, EMPTY, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, shareReplay, take, takeUntil, tap } from 'rxjs/operators';
import { ImplantBasedDentureModalService } from './components/rx-intuitive-implant-based-denture-modal/implant-based-denture-modal.service';
import { ImplantPositionModalService } from './components/rx-intuitive-implant-position-modal/implant-position-modal.service';
import type {
	RxIntuitiveImplantsViewModalOpenParams,
	RxIntuitiveImplantsViewModalResult,
	ToothForImplantsViewModal
} from './components/rx-intuitive-implants-view-modal/rx-intuitive-implants-view-modal.model';
import { RxIntuitiveImplantsViewModalService } from './components/rx-intuitive-implants-view-modal/rx-intuitive-implants-view-modal.service';
import {
	compareTreatmentListItems,
	getToothNumberById,
	IntuitiveToothForDiagram,
	IntuitiveToothForTreatment,
	TeethNumberingSystem,
	type DiagramToothAndBridgeListViewItem
} from './models';
import { indicationsWithoutDetails } from './models/consts';
import { IntuitiveBridge, IntuitiveBridgeValidationErrors } from './models/intuitive-bridge';
import { IntuitiveIndication, toUnitTypeOrDefault } from './models/intuitive-indication';
import { ToothModalOpenOptions, ToothModalResult, ToothModalService, TOOTH_MODAL_ACTION } from './models/tooth-modal';
import { RxIntuitiveBridgeService } from './services/rx-intuitive-bridge-service';
import { RxIntuitiveCopyFromBehaviorService } from './services/rx-intuitive-copy-from-behavior.service';
import { RxIntuitiveIndicationRulesService } from './services/rx-intuitive-indication-rules.service';
import { RxIntuitiveTeethImagesService } from './services/rx-intuitive-teeth-images.service';
import { RxIntuitiveToothTreatmentMapper } from './services/rx-intuitive-tooth-treatment-mapper.service';
import { RxIntuitiveTeethDiagramQuery } from './state/rx-intuitive-teeth-diagram-query';
import { RxIntuitiveTeethDiagramStore } from './state/rx-intuitive-teeth-diagram-store';
import { buildTreatmentListPreviewTitle, normalizeImplantsSummaryLine } from './utils/build-treatment-list-preview-title';
import { buildBridgePreviewLine, buildTreatmentPreviewLine } from './utils/build-treatment-preview-line';
import { ValidateRxService } from '@shared/services/validate-rx';

/**
 * Configuration for unit types that require modal dialogs instead of side tooth editor.
 */

const isIndicationWithModals = (indication: IntuitiveIndication, isGlidewell: boolean): boolean => {
	const INDICATIONS_WITH_MODALS: IntuitiveIndication[] = [IntuitiveIndication.ImplantPosition, IntuitiveIndication.ImplantBased];

	return !isGlidewell && INDICATIONS_WITH_MODALS.includes(indication);
};

@Injectable()
export class RxIntuitiveTeethDiagramFacade implements OnDestroy {
	readonly facadeName = 'RxIntuitiveTeethDiagramFacade';
	private readonly facadeDestroy$ = new Subject<void>();

	readonly teethNumberingSystem$ = combineLatest({
		userSettings: this.shellQuery.userSettings$,
		rx: this.shellQuery.rx$,
		forceDisplayTeethNumberingSystem: this.teethDiagramQuery.forceDisplayTeethNumberingSystem$
	}).pipe(
		map(({ userSettings, rx, forceDisplayTeethNumberingSystem }) =>
			getTeethNumberingSystem({
				forceTeethNumberingSystem: forceDisplayTeethNumberingSystem,
				teethNumberingSystemFromUserSettings: userSettings?.ToothId,
				teethNumberingSystemFromRx: rx?.ToothNumberingSystem,
				isDoctor: this.shellQuery.userRole === RoleTypeEnum.Doctor,
				logger: this.logger
			})
		),
		tap(system => this.teethDiagramStore.update({ teethNumberingSystem: system })),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly upperJawBridges$: Observable<Record<number, IntuitiveBridge>> = this.teethDiagramQuery.upperJaw$.pipe(
		map(jaw => {
			return this.bridgeService.getAllBridgesForJawIntuitive(jaw);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly lowerJawBridges$: Observable<Record<number, IntuitiveBridge>> = this.teethDiagramQuery.lowerJaw$.pipe(
		map(jaw => {
			return this.bridgeService.getAllBridgesForJawIntuitive(jaw);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly bridges$: Observable<Record<number, IntuitiveBridge>> = combineLatest([this.upperJawBridges$, this.lowerJawBridges$]).pipe(
		map(([upperJawBridges, lowerJawBridges]) => {
			return { ...upperJawBridges, ...lowerJawBridges };
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/**
	 * `procedureMap$` is included so jaw diagram models refresh when procedure context changes even if tooth rows are unchanged
	 * (default indication / denture rules).
	 */
	readonly upperJaw$: Observable<IntuitiveToothForDiagram[]> = combineLatest([
		this.teethDiagramQuery.upperJaw$,
		this.intuitiveDiagramQuery.selectedToothIds$,
		this.teethNumberingSystem$,
		this.orderQuery.procedureMap$,
		this.bridges$
	]).pipe(
		map(([jaw, selectedToothIds, teethNumberingSystem, , bridges]) =>
			jaw.map(t => {
				const bridge = bridges && t.BridgeIndex ? bridges[t.BridgeIndex] : undefined;

				return this.toDiagramVM(t, selectedToothIds, teethNumberingSystem, bridge);
			})
		)
	);

	/** See `upperJaw$` — same dependency on `procedureMap$`. */
	readonly lowerJaw$: Observable<IntuitiveToothForDiagram[]> = combineLatest([
		this.teethDiagramQuery.lowerJaw$,
		this.intuitiveDiagramQuery.selectedToothIds$,
		this.teethNumberingSystem$,
		this.orderQuery.procedureMap$,
		this.bridges$
	]).pipe(
		map(([jaw, selectedToothIds, teethNumberingSystem, , bridges]) =>
			jaw.map(t => {
				const bridge = bridges && t.BridgeIndex ? bridges[t.BridgeIndex] : undefined;

				return this.toDiagramVM(t, selectedToothIds, teethNumberingSystem, bridge);
			})
		)
	);

	protected readonly selectedTeeth$: Observable<Tooth[]> = combineLatest([
		this.teethDiagramQuery.teethRecord$,
		this.intuitiveDiagramQuery.selectedToothIds$
	]).pipe(
		map(([teethRecord, selectedToothIds]) => this.getTeethByIds(selectedToothIds, teethRecord)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly editorTeeth$: Observable<IntuitiveToothForTreatment[]> = combineLatest([
		this.selectedTeeth$,
		this.teethNumberingSystem$,
		this.intuitiveDiagramQuery.toothLastTouchedAt$
	]).pipe(
		map(([selectedTeeth, teethNumberingSystem, toothLastTouchedAt]: [Tooth[], TeethNumberingSystem, Record<number, number>]) =>
			selectedTeeth
				.map(t => this.toothTreatmentMapper.toIntuitiveToothForTreatment(t, teethNumberingSystem, toothLastTouchedAt, true))
				.sort((a, b) => b.lastTouchedAt - a.lastTouchedAt || a.toothId - b.toothId)
		)
	);

	/**
	 * All teeth with a non-Regular indication, sorted by most recently touched first (session map), then ascending tooth id.
	 */
	private readonly previewTeeth$: Observable<IntuitiveToothForTreatment[]> = combineLatest([
		this.teethDiagramQuery.teethFlat$,
		this.teethNumberingSystem$,
		this.intuitiveDiagramQuery.toothLastTouchedAt$
	]).pipe(
		map(([teeth, teethNumberingSystem, toothLastTouchedAt]: [Tooth[], TeethNumberingSystem, Record<number, number>]) => {
			return teeth
				.filter(t => t.ToothID != null && !this.indicationsService.isDefaultUnitType(t.UnitTypeID) && t.BridgeIndex == null)
				.map(t => this.toothTreatmentMapper.toIntuitiveToothForTreatment(t, teethNumberingSystem, toothLastTouchedAt, true))
				.sort((a, b) => b.lastTouchedAt - a.lastTouchedAt || a.toothId - b.toothId);
		})
	);

	/**
	 * Side-panel treatment list: non-Regular preview teeth and saved bridge summaries in one list,
	 * sorted like {@link previewTeeth$} with bridge tie-break on outer-left tooth id
	 * ({@link DiagramToothAndBridgeListViewItem.startToothId}).
	 */
	readonly previewTreatmentListItems$: Observable<DiagramToothAndBridgeListViewItem[]> = combineLatest([
		this.previewTeeth$,
		this.bridges$,
		this.teethNumberingSystem$,
		this.orderQuery.procedureMap$,
		this.intuitiveDiagramQuery.toothLastTouchedAt$
	]).pipe(
		map(([teeth, bridges, teethNumberingSystem, procedureMap, toothLastTouchedAt]) => {
			const toothItems = teeth.map(tooth => this.mapPreviewToothToDiagramListItem(tooth));

			let bridgeItems: DiagramToothAndBridgeListViewItem[] = [];

			if ((procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.FixedRestorative) {
				bridgeItems = Object.values(bridges).map(bridge =>
					this.mapBridgeToDiagramListItem(bridge, teethNumberingSystem, toothLastTouchedAt)
				);
			}

			return [...toothItems, ...bridgeItems].sort(compareTreatmentListItems);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/** Single indication ID when all selected teeth have the same non-null indication; otherwise []. */
	readonly selectedTeethIndicationIds$: Observable<IntuitiveIndication[]> = this.selectedTeeth$.pipe(
		map(selected => {
			if (selected.length === 0) {
				return [];
			}

			const effectiveIds = selected.map(t => this.indicationsService.getUnitTypeIndicationForDiagram(t.UnitTypeID));
			const firstEffective = effectiveIds[0];

			if (firstEffective == null) {
				return [];
			}

			return effectiveIds.every(id => id === firstEffective) ? [firstEffective] : [];
		})
	);

	readonly selectedToothIds$ = this.intuitiveDiagramQuery.selectedToothIds$;
	readonly hasSelectedTeeth$: Observable<boolean> = this.selectedToothIds$.pipe(map(ids => ids.length > 0));

	/**
	 * Show side tooth editor for unit types that:
	 * - Have indication (not DEFAULT INDICATION)
	 * - Don't use modal dialogs (not in INDICATIONS_WITH_MODALS)
	 */
	readonly isToothEditorShown$: Observable<boolean> = this.editorTeeth$.pipe(
		map(editorTeeth => {
			const teethWithIndication = editorTeeth.filter(t => !this.indicationsService.isDefaultIndication(t.indicationId));

			if (teethWithIndication.length === 0) {
				return false;
			}

			return !isIndicationWithModals(teethWithIndication[0].indicationId, this.orderQuery.isGlidewellOrder);
		})
	);

	protected readonly toothEditorReadOnly$ = combineLatest([
		this.shellQuery.getReadOnlyState('toothEditor'),
		this.hostPlatformService.isIteroLab$
	]).pipe(map(([isReadOnly, isIteroLab]) => isReadOnly || isIteroLab));

	/** Matches {@link ReadOnlyRulesService#isTeethDiagramReadOnly$} — chart selection / indications off when read-only. */
	readonly isTeethDiagramReadOnly$: Observable<boolean> = combineLatest([
		this.shellQuery.getReadOnlyState('teethDiagram'),
		this.hostPlatformService.isIteroLab$
	]).pipe(map(([isReadOnly, isIteroLab]) => isReadOnly || isIteroLab));

	/**
	 * Determines if the tooth editor fields can be edited.
	 * Returns false when all selected teeth are unit types without details, or when shell read-only / iTero Lab.
	 */
	readonly canEditToothEditor$: Observable<boolean> = combineLatest([
		this.editorTeeth$.pipe(map(editorTeeth => this.computeCanEditToothEditor(editorTeeth))),
		this.toothEditorReadOnly$
	]).pipe(map(([canEditByUnitType, readOnly]) => canEditByUnitType && !readOnly));

	/** Delete control in the tooth editor header — off in shell read-only (same gate as {@link #canEditToothEditor$}). */
	readonly canDeleteToothEditor$: Observable<boolean> = combineLatest([this.isToothEditorShown$, this.toothEditorReadOnly$]).pipe(
		map(([editorShown, readOnly]) => editorShown && !readOnly)
	);

	/**
	 * Observable that fetches the teeth from the shell query and updates the teeth diagram store.
	 * This is used to update the teeth diagram when the Rx or procedure is changed in the shell query.
	 */
	readonly fetchRxAndSyncStore$ = combineQueries([this.shellQuery.teeth$, this.shellQuery.isProcedureChanged$]).pipe(
		filter(([, isProcedureChanged]) => !isProcedureChanged),
		map(([teeth]) => teeth),
		tap((teeth: Tooth[]) => {
			if (!!teeth) {
				this.teethDiagramStore.resetTeeth();
				this.teethDiagramStore.updateTeeth({ teethToUpdate: teeth });
				this.intuitiveDiagramStore.update({ toothLastTouchedAt: {} });
			}
		})
	);

	/**
	 * Observable that fetches the complete list of indications available for the current procedure.
	 */
	readonly visibleIndications$: Observable<IntuitiveIndication[]> = combineLatest([
		this.orderQuery.procedureMap$,
		this.shellQuery.rxConfiguration$,
		this.hostPlatformService.isIteroModeling$
	]).pipe(
		map(([procedureMap, rxConfiguration, isIteroModeling]) => {
			const withBridges = (procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.FixedRestorative;

			return this.indicationsService.getProcedureIndications(procedureMap, rxConfiguration, isIteroModeling, withBridges);
		})
	);

	/**
	 * Toolbar indication list: full procedure list when editable; in diagram read-only, distinct
	 * non-default indications present on **any tooth** in the diagram (chart selection is often empty
	 * when read-only). Order matches {@link #visibleIndications$}.
	 */
	readonly indicationToolbarIds$: Observable<IntuitiveIndication[]> = combineLatest([
		this.isTeethDiagramReadOnly$,
		this.visibleIndications$,
		this.teethDiagramQuery.teethFlat$
	]).pipe(
		map(([readOnly, visible, teethFlat]) => {
			if (!readOnly) {
				return visible;
			}

			const distinct = new Set<IntuitiveIndication>();

			for (const t of teethFlat ?? []) {
				const id = t.ToothInBridgeTypeID
					? IntuitiveIndication.Bridge
					: this.indicationsService.getUnitTypeIndicationForDiagram(t.UnitTypeID);

				if (!this.indicationsService.isDefaultIndication(id)) {
					distinct.add(id);
				}
			}

			return visible.filter(ind => distinct.has(ind));
		}),
		distinctUntilChanged((a, b) => a.length === b.length && a.every((value, index) => value === b[index]))
	);

	/**
	 * Observable that fetches the list of indications which should be enabled for the selected teeth in the current procedure.
	 */
	readonly enabledIndications$: Observable<IntuitiveIndication[]> = combineLatest([
		this.selectedTeeth$,
		this.teethDiagramQuery.upperJaw$,
		this.teethDiagramQuery.lowerJaw$,
		this.orderQuery.procedureMap$,
		this.shellQuery.rxConfiguration$,
		this.hostPlatformService.isIteroModeling$
	]).pipe(
		map(([selectedTeeth, upperJaw, lowerJaw, procedureMap, rxConfiguration, isIteroModeling]) => {
			const canCreateBridge = !!this.bridgeService.canCreateBridgeFromTeeth(selectedTeeth, { upperJaw, lowerJaw });
			const withBridges = canCreateBridge !== false;

			return this.indicationsService.getCommonIndicationsForTeeth(
				selectedTeeth,
				procedureMap,
				rxConfiguration,
				isIteroModeling,
				withBridges
			);
		})
	);
	/** Combined prefixed sprite SVG markup for injection next to the diagram. */
	readonly injectedSpritesContent$: Observable<Map<string, SafeHtml>>;
	private readonly injectedSpritesContentSubject: BehaviorSubject<Map<string, SafeHtml>>;

	private readonly loadingSpriteFolders = new Set<string>();
	private readonly accumulatedSpriteMarkup: Map<string, SafeHtml> = new Map<string, SafeHtml>();

	constructor(
		protected readonly intuitiveDiagramStore: RxIntuitiveTeethDiagramStore,
		protected readonly intuitiveDiagramQuery: RxIntuitiveTeethDiagramQuery,
		protected readonly teethDiagramQuery: TeethDiagramQuery,
		protected readonly teethDiagramStore: TeethDiagramStore,
		protected readonly logger: LoggerService,
		protected readonly imagesService: RxIntuitiveTeethImagesService,
		protected readonly shellQuery: ShellQuery,
		protected readonly orderQuery: OrderQuery,
		protected readonly indicationsService: RxIntuitiveIndicationRulesService,
		protected readonly hostPlatformService: HostPlatformService,
		private readonly implantPositionModalService: ImplantPositionModalService,
		private readonly implantBasedDentureModalService: ImplantBasedDentureModalService,
		protected readonly bridgeService: RxIntuitiveBridgeService,
		protected readonly toothTreatmentMapper: RxIntuitiveToothTreatmentMapper,
		private readonly copyFromBehaviorService: RxIntuitiveCopyFromBehaviorService,
		protected readonly eventPublishService: EventPublisherService,
		protected readonly implantsViewModalService: RxIntuitiveImplantsViewModalService,
		protected readonly translate: TranslateService,
		private readonly teethManagerService: TeethManagerService,
		protected readonly implantsViewService: ImplantsViewService,
		private readonly validateRxService: ValidateRxService
	) {
		this.injectedSpritesContentSubject = new BehaviorSubject(new Map<string, SafeHtml>());
		this.injectedSpritesContent$ = this.injectedSpritesContentSubject.asObservable();
	}

	ngOnDestroy(): void {
		this.facadeDestroy$.next();
		this.facadeDestroy$.complete();
	}

	/**
	 * Loads sprite sheets for all indication folders needed by the current jaws (plus regular).
	 * Call when upper/lower jaw diagram models change (e.g. from container subscription).
	 */
	syncInjectedSpritesContent(upperJaw: IntuitiveToothForDiagram[], lowerJaw: IntuitiveToothForDiagram[]): void {
		const allTeeth = [...upperJaw, ...lowerJaw];
		const uniqueIndicationIds = new Set<IntuitiveIndication>();

		for (const t of allTeeth) {
			uniqueIndicationIds.add(t.indicationId);
		}

		type SpriteFolderIndicationId = Parameters<RxIntuitiveTeethImagesService['getSpriteFolder']>[0];

		const loadSpriteIfNeeded = (indicationId: SpriteFolderIndicationId): void => {
			const folder = this.imagesService.getSpriteFolder(indicationId);

			if (this.accumulatedSpriteMarkup.has(folder) || this.loadingSpriteFolders.has(folder)) {
				return;
			}

			this.loadingSpriteFolders.add(folder);
			this.imagesService
				.getSpriteContent(indicationId)
				.pipe(
					tap(safeHtml => {
						this.accumulatedSpriteMarkup.set(folder, safeHtml);
						this.injectedSpritesContentSubject.next(new Map<string, SafeHtml>(this.accumulatedSpriteMarkup.entries()));
					}),
					takeUntil(this.facadeDestroy$),
					finalize(() => this.loadingSpriteFolders.delete(folder))
				)
				.subscribe();
		};

		for (const indicationId of uniqueIndicationIds) {
			loadSpriteIfNeeded(indicationId);
		}

		loadSpriteIfNeeded(null);
	}

	clearToothSelection(): void {
		this.intuitiveDiagramStore.update({ selectedToothIds: [] });
	}

	copyTreatmentDetailsFromSourceTooth(sourceToothId: number): void {
		this.copyFromBehaviorService.copyTreatmentDetailsFromSourceTooth(sourceToothId);
	}

	toggleToothSelection(toothId: number): void {
		const teeth = this.teethDiagramQuery.teethRecord;
		const selectedToothIds = this.intuitiveDiagramQuery.selectedToothIds;
		const tooth = teeth[toothId];

		if (tooth?.BridgeIndex) {
			const isBridgeMode = !!this.intuitiveDiagramQuery.selectedBridge;
			const bridge = this.bridgeService.getBridgeSpanByBridgeIndex(tooth.BridgeIndex, this.teethDiagramQuery.teethFlat);

			if (bridge && !isBridgeMode) {
				return this.intuitiveDiagramStore.selectBridge(bridge, toothId);
			}
		}

		let next: number[];

		if (selectedToothIds.includes(toothId)) {
			next = selectedToothIds.filter(id => id !== toothId);
		} else if (
			// Exclusive selection for non-default types and for default teeth that qualify as copy-from sources.
			!this.indicationsService.isDefaultUnitType(tooth?.UnitTypeID) ||
			(tooth != null && this.copyFromBehaviorService.isCopyFromSourceTooth(tooth))
		) {
			next = [toothId];
		} else {
			next = selectedToothIds.filter(id => this.indicationsService.isDefaultUnitType(teeth[id]?.UnitTypeID));
			next = [...next, toothId];
		}

		this.intuitiveDiagramStore.update({ selectedToothIds: next });
	}

	assignIndication(indicationId: IntuitiveIndication, skipResetTooth = false): void {
		const selectedToothIds = this.intuitiveDiagramQuery.selectedToothIds;

		if (selectedToothIds.length === 0) {
			this.logger.warn('assignIndication called with no teeth selected', {
				module: this.facadeName
			});

			return;
		}

		if (indicationId === IntuitiveIndication.Bridge) {
			this.createBridge(selectedToothIds);

			return;
		}

		/**
		 * For indications that use a modal, we do not automatically reset the values of selected teeth.
		 * This ensures that if the user cancels the modal, the teeth keep their previous values.
		 * Because of this, these teeth values must be reset manually in the handler of modal's save action.
		 */
		if (skipResetTooth) {
			return;
		}

		const unitType = toUnitTypeOrDefault(indicationId, this.indicationsService.getDefaultUnitType());

		this.teethDiagramStore.resetIndicationByIds(selectedToothIds, unitType);
		this.intuitiveDiagramStore.markRecentlyEditedTeeth(selectedToothIds);
	}

	createBridge(selectedToothIds: number[]): void {
		const bridge = this.bridgeService.createBridgeTeethSpan(selectedToothIds, this.teethDiagramQuery.teeth);

		if (bridge) {
			this.intuitiveDiagramStore.selectBridge(bridge);
			this.intuitiveDiagramStore.markRecentlyEditedTeeth(bridge.teeth.map(t => t.ToothID).filter((id): id is number => id != null));
		}
	}

	openTreatmentEditorFromList(toothId: number): void {
		const teethRecord = this.teethDiagramQuery.teethRecord;
		const tooth = this.getToothById(toothId, teethRecord);

		if (tooth?.BridgeIndex) {
			const bridge = this.bridgeService.getBridgeSpanByBridgeIndex(tooth.BridgeIndex, this.teethDiagramQuery.teethFlat);

			if (bridge) {
				return this.intuitiveDiagramStore.selectBridge(bridge, toothId);
			}
		}

		if (tooth?.UnitTypeID) {
			this.intuitiveDiagramStore.update({ selectedToothIds: [toothId] });

			const modalResult$ = this.openModalForUnitTypeIfNeeded(
				this.indicationsService.getUnitTypeIndicationForDiagram(tooth.UnitTypeID)
			);

			if (modalResult$) {
				modalResult$.pipe(takeUntil(this.facadeDestroy$)).subscribe();
			}
		}
		// Otherwise, side editor will be shown reactively via isToothEditorShown$
	}

	/**
	 * Opens appropriate tooth modal based on the unit type (ImplantPosition; ImplantBased denture flow;
	 * ImplantBased non-denture opens the embedded implants-view modal here).
	 * Returns an Observable that emits the modal result action, or EMPTY if no modal is needed.
	 */
	openModalForUnitTypeIfNeeded(indication: IntuitiveIndication): Observable<ToothModalResult> | null {
		if (!isIndicationWithModals(indication, this.orderQuery.isGlidewellOrder)) {
			return null;
		}

		return this.openModalForUnitTypeIfNeededInternal(indication);
	}

	resetIndicationForSelectedTeeth(): void {
		const teeth = this.teethDiagramQuery.teethRecord;
		const selectedToothIds = this.intuitiveDiagramQuery.selectedToothIds;
		const toothIdsToReset = selectedToothIds.filter(id => !this.indicationsService.isDefaultUnitType(teeth[id]?.UnitTypeID));

		this.resetIndicationForTeeth(toothIdsToReset);

		if (toothIdsToReset.length > 0) {
			this.intuitiveDiagramStore.update({ selectedToothIds: [] });
		}
	}

	resetIndicationForTeeth(toothIdsToReset: number[]): void {
		if (toothIdsToReset.length === 0) {
			return;
		}

		const defaultUnitType = this.indicationsService.getDefaultUnitType();

		// Copy-from: full reset (not only UnitTypeID) so restorative fields do not linger on Regular teeth.
		this.teethDiagramStore.resetIndicationByIds(toothIdsToReset, defaultUnitType);
		this.intuitiveDiagramStore.markRecentlyEditedTeeth(toothIdsToReset);
	}

	updateToothCrownData(crownForm: CrownForm): void {
		this.teethDiagramStore.updateTeethByIds(crownForm.teethIds, {
			MaterialID: crownForm.material,
			ShadeSystemId: crownForm.shadeSystem,
			ShadeBody: crownForm.shadeBody,
			SpecificationId: crownForm.specification
		});
		this.intuitiveDiagramStore.markRecentlyEditedTeeth(crownForm.teethIds);
	}

	applyToothAdditionalInfo(toothIds: number[], result: AdditionalInfoResult): void {
		this.teethDiagramStore.updateTeethByIds(toothIds, result.values);
		this.intuitiveDiagramStore.markRecentlyEditedTeeth(toothIds);
		this.markCrownControlsAsRemovedByUser(toothIds, result.clearedFields);
	}

	protected markCrownControlsAsRemovedByUser(teethIds: number[], clearedFields: AdditionalInfoFieldKey[]): void {
		if (clearedFields.length > 0) {
			teethIds.forEach(id => {
				this.teethManagerService.markCrownControlsAsRemovedByUser({ ToothID: id }, clearedFields);
			});
		}
	}

	clearDisabledToothFields(toothIds: number[], payload: Partial<Record<string, unknown>>): void {
		this.teethDiagramStore.updateTeethByIds(toothIds, payload);
	}

	protected openModalForUnitTypeIfNeededInternal(indication: IntuitiveIndication): Observable<ToothModalResult> {
		if (indication === IntuitiveIndication.ImplantBased) {
			const shouldPrevent = this.implantsViewService.shouldPreventImplantBasedSelection(indication);

			if (shouldPrevent) {
				return EMPTY;
			}
		}

		const selectedToothIds = this.intuitiveDiagramQuery.selectedToothIds;

		if (selectedToothIds.length === 0) {
			return EMPTY;
		}

		const teethRecord = this.teethDiagramQuery.teethRecord;
		const initialTooth = this.getToothById(selectedToothIds[0], teethRecord);

		if (!initialTooth) {
			return EMPTY;
		}

		const teethNumberingSystem = this.teethDiagramQuery.teethNumberingSystem;
		const teethForModal = selectedToothIds
			.map(id => this.getToothById(id, teethRecord))
			.filter((tooth): tooth is Tooth => tooth != null)
			.map(tooth => this.toDiagramVM(tooth, [], teethNumberingSystem));

		let modal$: Observable<ToothModalResult>;

		switch (indication) {
			case IntuitiveIndication.ImplantPosition: {
				modal$ = this.implantsViewService.checkIfNewImplantsViewRequired()
					? this.openImplantsViewModal(selectedToothIds, IntuitiveIndication.ImplantPosition)
					: this.openToothModal(
							this.implantPositionModalService,
							teethForModal,
							initialTooth,
							IntuitiveIndication.ImplantPosition
						);
				break;
			}
			case IntuitiveIndication.ImplantBased: {
				modal$ = this.implantsViewService.checkIfNewImplantsViewRequired()
					? this.openImplantsViewModal(selectedToothIds, IntuitiveIndication.ImplantBased)
					: this.openToothModal(
							this.implantBasedDentureModalService,
							teethForModal,
							initialTooth,
							IntuitiveIndication.ImplantBased
						);
				break;
			}
			default:
				this.logger.warn(`No modal configured for indication: ${indication}`, {
					module: this.facadeName
				});

				return EMPTY;
		}

		return defer(() => {
			return modal$;
		});
	}

	private openImplantsViewModal(
		selectedToothIds: number[],
		indicationId: IntuitiveIndication
	): Observable<RxIntuitiveImplantsViewModalResult> {
		const openParams = this.buildImplantsViewModalOpenParams(this.teethDiagramQuery.teethFlat, indicationId);

		return this.implantsViewModalService.open(openParams).pipe(
			take(1),
			tap(result => {
				if (result.action === TOOTH_MODAL_ACTION.Save && result.payload.toothPartialUpdate != null) {
					this.updateTeethByIdsForIndicationWithModal(selectedToothIds, result.payload.toothPartialUpdate, indicationId);

					this.markCrownControlsAsRemovedByUser(
						selectedToothIds,
						result.payload.pendingAdditionalInfoClears?.flatMap(entry => entry.clearedFields) ?? []
					);

					return;
				}

				if (result.action === TOOTH_MODAL_ACTION.Delete) {
					this.resetIndicationForTeeth(selectedToothIds);
				}
			})
		);
	}

	/**
	 *
	 * @param teethSource - The source of teeth.
	 * - For regular mode, teeth source is from teethDiagramQuery.teethFlat.
	 * - For bridge mode, teeth source is from intuitiveDiagramQuery.selectedBridgeTeeth.
	 * @param requestedIndication - Indication the user selected to open the modal.
	 * The tooth may still have its previous unit type until the modal saves and {@link assignIndication} runs.
	 */
	protected buildImplantsViewModalOpenParams(
		teethSource: Tooth[],
		requestedIndication: IntuitiveIndication
	): RxIntuitiveImplantsViewModalOpenParams {
		const selectedToothIds = this.intuitiveDiagramQuery.selectedToothIds;
		const teethNumberingSystem = this.teethDiagramQuery.teethNumberingSystem;

		const teethForModal: ToothForImplantsViewModal[] = selectedToothIds
			.map(id => {
				const tooth = teethSource.find(t => t.ToothID === id);

				if (!tooth) {
					return null;
				}

				return {
					toothId: tooth.ToothID ?? 0,
					indicationId: requestedIndication,
					bridgeId: tooth.BridgeIndex,
					toothNumberInSystem: getToothNumberById({ toothId: tooth.ToothID ?? 0, teethNumberingSystem }),
					customLabMaterialGroup: tooth.CustomLabMaterialGroup,
					implantsViewData: tooth.ImplantsViewData,
					restorationTypeIds: {
						ImplantBasedRestorationTypeId: tooth.ImplantBasedRestorationTypeId,
						AbutmentType: tooth.AbutmentType,
						AbutmentMaterialId: tooth.AbutmentMaterialId
					},
					material: tooth.MaterialID,
					shadeSystem: tooth.ShadeSystemId,
					shadeBody: tooth.ShadeBody,
					specification: tooth.SpecificationId,
					additionalInfoIds: extractAdditionalInfoIds(tooth)
				} satisfies ToothForImplantsViewModal;
			})
			.filter((tooth): tooth is NonNullable<typeof tooth> => tooth != null);

		const clinicalNumbersJoined = selectedToothIds.map(id => getToothNumberById({ toothId: id, teethNumberingSystem })).join(', ');
		const toothLabel = this.translate.instant('ToothEditor.Tooth') as string;

		return {
			teeth: teethForModal,
			headerToothTitle: `${toothLabel} ${clinicalNumbersJoined}`,
			procedureId: this.orderQuery.procedure?.Id,
			procedureMapTypeId: this.orderQuery.procedureMap?.TypeId,
			isBridge: requestedIndication === IntuitiveIndication.BridgeImplantBased,
			shouldValidateForSend$: this.shellQuery.shouldValidateForSend$,
			isReadOnly$: combineLatest([this.shellQuery.getReadOnlyState('toothEditor'), this.hostPlatformService.isIteroLab$]).pipe(
				map(([isReadOnly, isIteroLab]) => isReadOnly || isIteroLab)
			)
		} satisfies RxIntuitiveImplantsViewModalOpenParams;
	}

	private updateTeethByIdsForIndicationWithModal(
		teethIds: number[],
		partialToothUpdate: Partial<Omit<Tooth, 'ToothID'>>,
		indicationId: IntuitiveIndication
	): void {
		this.teethDiagramStore.resetIndicationByIds(
			teethIds,
			toUnitTypeOrDefault(indicationId, this.indicationsService.getDefaultUnitType())
		);
		this.teethDiagramStore.updateTeethByIds(teethIds, partialToothUpdate);
		this.intuitiveDiagramStore.markRecentlyEditedTeeth(teethIds);
	}

	/**
	 * Generic handler for opening tooth modals.
	 * Returns the modal result observable with side effects (store updates) applied via tap.
	 * @param modalService - Service implementing ToothModalService interface
	 * @param teethForModal - View models to display in modal
	 * @param initialTooth - Initial tooth data (typically from first selected tooth)
	 * @param modalName - Name of modal for logging purposes
	 */
	private openToothModal(
		modalService: ToothModalService,
		teethForModal: IntuitiveToothForDiagram[],
		initialTooth: Tooth,
		indicationId: IntuitiveIndication,
		options?: ToothModalOpenOptions
	): Observable<ToothModalResult> {
		return modalService.open(teethForModal, initialTooth, options).pipe(
			take(1),
			tap(result => {
				if (result.action === TOOTH_MODAL_ACTION.Save && result.payload.toothPartialUpdate != null) {
					const toothIdsToUpdate = teethForModal.map(t => t.toothId);

					this.teethDiagramStore.updateTeethByIds(toothIdsToUpdate, result.payload.toothPartialUpdate);
					this.intuitiveDiagramStore.markRecentlyEditedTeeth(toothIdsToUpdate);

					this.logger.info(`Indication ${indicationId}'s modal closed with changes`, {
						module: this.facadeName,
						extendedParameters: { count: String(toothIdsToUpdate.length) }
					});
				}
			})
		);
	}

	protected toDiagramVM(
		tooth: Tooth,
		selectedToothIds: number[],
		teethNumberingSystem: TeethNumberingSystem,
		bridge?: IntuitiveBridge,
		validationError?: IntuitiveBridgeValidationErrors
	): IntuitiveToothForDiagram {
		const indicationId = this.getToothIndication(tooth, bridge);

		const toothNumberInSystem = getToothNumberById({
			toothId: tooth.ToothID ?? 0,
			teethNumberingSystem
		}) as ReturnType<typeof getToothNumberById> | undefined;

		return {
			toothId: tooth.ToothID ?? 0,
			toothNumberInSystem: toothNumberInSystem ?? 0,
			indicationId,
			svgSymbolId: this.imagesService.getSymbolId(tooth.ToothID ?? 0, indicationId),
			isSelected: selectedToothIds.includes(tooth.ToothID ?? 0),
			bridgeId: bridge?.index,
			bridge: bridge
				? {
						index: bridge.index,
						isOuterLeft: bridge.outerLeftToothId === tooth.ToothID,
						isOuterRight: bridge.outerRightToothId === tooth.ToothID
					}
				: undefined,
			isInvalid: tooth.ToothID != null && !!validationError?.teeth?.[tooth.ToothID]?.length,
			isInvalidForSend: this.validateToothForSend(tooth, bridge)
		};
	}

	private validateToothForSend(tooth: Tooth, bridge?: IntuitiveBridge): boolean {
		if (!this.shellQuery.shouldValidateForSend) {
			return false;
		}

		const toothIndication = this.getToothIndication(tooth, bridge);
		const isDefaultIndication = this.indicationsService.isDefaultIndication(toothIndication);

		return isDefaultIndication ? false : this.validateRxService.isTreatmentInvalidForSend(tooth);
	}

	private getToothIndication(tooth: Tooth, bridge?: IntuitiveBridge): IntuitiveIndication {
		if (!bridge) {
			return this.indicationsService.getUnitTypeIndicationForDiagram(tooth.UnitTypeID);
		}

		const bridgeTooth = bridge.teeth.filter(t => t.ToothID === tooth.ToothID)[0];

		return this.indicationsService.getBridgeIndicationForDiagram(bridgeTooth);
	}

	/** Gets multiple teeth by IDs using O(1) record lookup */
	private getTeethByIds(toothIds: number[], teethRecord: Record<number, Tooth>): Tooth[] {
		return toothIds.map(id => teethRecord[id]).filter((t): t is Tooth => t != null);
	}

	/** Gets single tooth by ID using O(1) record lookup */
	private getToothById(toothId: number, teethRecord: Record<number, Tooth>): Tooth | undefined {
		return teethRecord[toothId];
	}

	private maxLastTouchedAtForBridgeTeeth(bridge: IntuitiveBridge, toothLastTouchedAt: Record<number, number>): number {
		let max = 0;

		for (const tooth of bridge.teeth) {
			const id = tooth.ToothID;

			if (id == null) {
				continue;
			}

			const touched = toothLastTouchedAt[id] ?? 0;

			if (touched > max) {
				max = touched;
			}
		}

		return max;
	}

	private mapPreviewToothToDiagramListItem(intuitiveTooth: IntuitiveToothForTreatment): DiagramToothAndBridgeListViewItem {
		const indicationId = intuitiveTooth.indicationId ?? this.indicationsService.getDefaultIndication();
		const previewLine = buildTreatmentPreviewLine(intuitiveTooth);
		const implantsSummaryLine = normalizeImplantsSummaryLine(intuitiveTooth.implantsTreatmentSummary);
		const previewTitle = buildTreatmentListPreviewTitle({
			indicationId,
			previewLine,
			implantsSummaryLine
		});

		const toothNumberInSystem = intuitiveTooth.toothNumberInSystem ?? '';
		const tooth = this.getToothById(intuitiveTooth.toothId, this.teethDiagramQuery.teethRecord);
		const isInvalid = tooth ? this.validateToothForSend(tooth) : false;

		return {
			startToothId: intuitiveTooth.toothId,
			previewLine,
			previewTitle,
			implantsSummaryLine,
			lastTouchedAt: intuitiveTooth.lastTouchedAt,
			indicationId,
			startToothNumberInSystem: toothNumberInSystem,
			isInvalid
		};
	}

	protected mapBridgeToDiagramListItem(
		bridge: IntuitiveBridge,
		teethNumberingSystem: TeethNumberingSystem,
		toothLastTouchedAt: Record<number, number>
	): DiagramToothAndBridgeListViewItem {
		const bridgeTeeth = bridge.teeth.map(tooth =>
			this.toothTreatmentMapper.toIntuitiveToothForTreatment(tooth, teethNumberingSystem, toothLastTouchedAt, true)
		);

		const isInvalid = bridge.teeth.some(tooth => this.validateToothForSend(tooth, bridge));

		const previewLine = buildBridgePreviewLine(bridgeTeeth);
		const outerLeftId = bridge.outerLeftToothId ?? 0;
		const outerRightId = bridge.outerRightToothId ?? 0;
		const firstId = Math.min(outerLeftId, outerRightId);
		const lastId = Math.max(outerLeftId, outerRightId);

		return {
			bridgeIndex: bridge.index,
			startToothId: outerLeftId,
			endToothId: outerRightId,
			previewLine,
			previewTitle: previewLine,
			lastTouchedAt: this.maxLastTouchedAtForBridgeTeeth(bridge, toothLastTouchedAt),
			indicationId: IntuitiveIndication.Bridge,
			startToothNumberInSystem: getToothNumberById({ toothId: firstId, teethNumberingSystem }),
			endToothNumberInSystem: getToothNumberById({ toothId: lastId, teethNumberingSystem }),
			isInvalid
		};
	}

	protected computeCanEditToothEditor(editorTeeth: IntuitiveToothForTreatment[]): boolean {
		const teethWithIndication = editorTeeth.filter(t => !this.indicationsService.isDefaultIndication(t.indicationId));

		if (teethWithIndication.length === 0) {
			return true;
		}

		return !indicationsWithoutDetails.includes(teethWithIndication[0].indicationId);
	}
}
